// Wrapper for permission logic.  Trying to move everything to here so we can have a single place.
import {
  ALL_PERMISSIONS,
  PermissionEnum,
  RoleMapType,
  hasPermission,
  securityGroupNameLevelMap,
} from '@orgbrain/roles';
import { sharedConfig } from '@orgbrain/shared-config';
import { BaseUser, CurrentUser, Session } from './lib-admin';
import * as Auth from './org-auth/org-auth-classes';
import * as Policy from './org-auth/policies';
import { getOrgFromCurrentUser } from './rescued-from-roles';

import {
  BackendErrorCodesEnum,
  Code,
  CredentialEnum,
  IdentityType,
  OrganizationPolicy,
  OrgbrainFatal,
  SecurityGroupName,
  default2FAArrayMap,
  makeIntoCredentialArray,
} from '@orgbrain/lib-data';
import { PreemptiveSecurityReport } from './classes';
import { AuthError } from './org-auth/org-auth-classes';

export class PermissionService {
  // This ensures user has auth for all permissions.
  // Returns null on success,
  // Returns rule result of failing rule if it does not pass. (This will contain the credentials required to pass the test.)
  // This is probably a function that is called from the front end. It is not used in the back end.
  static preemptiveOrgPermissionAuthCheck({
    preemptivePermissionLevelName,
    currentUser,
    organizationId,
    // testPolicy, // Optional policy to test against instead of the one in organization
    preemptiveReport,
  }: {
    // Note: This is the same as the function below, but with a different name.  This is the one that is called.
    preemptivePermissionLevelName: SecurityGroupName;
    currentUser: CurrentUser;
    organizationId: string;
    // testPolicy?: OrganizationPolicy; // Optional policy to test against instead of the one in organization
    preemptiveReport: PreemptiveSecurityReport;
  }): Auth.RuleResult {
    // If any permission passes we are OK.
    const thisOrg = getOrgFromCurrentUser({ currentUser, organizationId });
    const orgPolicy: OrganizationPolicy =
      preemptiveReport?.testPolicy || thisOrg?.authPolicy || null;

    if (!orgPolicy) {
      return { pass: true, expires: null, reason: 'No org policy' };
    }

    const policy: Auth.Policy = Policy.createPolicy(orgPolicy);
    // preemptiveReport.policy = policy;
    const userPermissions = PermissionService.userPermissionsInOrg({
      currentUser,
      organizationId,
    });

    // const permissionMap = permissions.reduce((acc, perm) => { acc[perm] = true; return acc }, {} as { [key in PermissionEnum]: boolean })

    let highLevel = -1;
    let highRule: Auth.SecurityGroupRule = null;
    let highPerm: PermissionEnum = null;
    const levelMax = securityGroupNameLevelMap[preemptivePermissionLevelName];
    for (const perm of userPermissions) {
      const ruleName: SecurityGroupName = policy.permissionSecurityGroupMap[perm];
      const rule: Auth.SecurityGroupRule = policy.securityGroupRuleMap[ruleName];
      const level: number = securityGroupNameLevelMap[ruleName];

      if (level > levelMax) {
        continue; // We only check up to preemptivePermissionLevel
      }

      if (level > highLevel) {
        highLevel = level;
        highRule = rule;
        highPerm = perm;
      }
    }

    let session: Session = preemptiveReport?.session || currentUser?.session;

    session = JSON.parse(JSON.stringify(session));

    if (!highRule) {
      console.error(' Expected to find a high rule.  This should not happen.');
    }

    if (highRule) {
      const atomicRule = Policy.atomicRuleMap[highRule.ruleName];
      const result: Auth.RuleResult = atomicRule.evaluate({
        session,
        params: highRule.params,
        preemptiveReport,
      });

      // if (result.pass) {
      preemptiveReport.result = result;
      //   return result;
      // }

      result.ruleName = highRule.ruleName;
      result.perm = highPerm;
      return result;
    }

    return {
      pass: true,
      expires: null,
      reason: 'Should not happen (preemptiveOrgPermissionAuthCheck)',
    };
  }

  // Test user has one of the given permissions in the given organization.
  // Then test the user has the required credentials for the organization poliy (or tetPolicy if given)
  static hasCurrentUserPermissions({
    permissions,
    currentUser,
    organizationId,
    throwError,
    testPolicy,
    preemptiveReport,
    allowSuper,
  }: {
    permissions: PermissionEnum[];
    currentUser: CurrentUser;
    organizationId: string;
    throwError: boolean;
    testPolicy?: OrganizationPolicy; // Optional policy to test against instead of the one in organization
    preemptiveReport?: PreemptiveSecurityReport;
    allowSuper?: boolean;
  }) {
    try {
      if (allowSuper && currentUser.superuser) {
        return true;
      }

      PermissionService.hasCurrentUserPermissionsOriginal({
        permissions,
        currentUser,
        organizationId,
      });

      // If any permission passes we are OK.
      const thisOrg = getOrgFromCurrentUser({ currentUser, organizationId });
      const orgPolicy: OrganizationPolicy = testPolicy || thisOrg?.authPolicy || null;

      const policy: Auth.Policy = Policy.createPolicy(orgPolicy);

      // Note here we see if a user has the authorization for any of the required permissions.
      // We use the list of required permissions even if the user has not got them.
      // If we did not do this we might endup asking for a higher level of security than is acutally required.
      PermissionService.userHasOrganizationAuthForPermissions({
        permissions,
        currentUser,
        policy,
        preemptiveReport,
      });
    } catch (error) {
      if (throwError) {
        throw error;
      }

      return false;
    }
    return true;
  }

  public static userPermissionsInOrg({
    currentUser,
    organizationId, // PLease use this.
  }: {
    currentUser: CurrentUser;
    organizationId: string;
  }): PermissionEnum[] {
    const thisOrg = getOrgFromCurrentUser({ currentUser, organizationId });
    const rolesIsOrg = (thisOrg && thisOrg.roles) || [];

    const roleMap: RoleMapType = (thisOrg && thisOrg.roleMap) || ({} as RoleMapType);

    if (!roleMap || Object.keys(roleMap).length === 0) {
      return [];
    }

    const userPermissions = [];

    for (const perm of ALL_PERMISSIONS) {
      if (hasPermission(perm, rolesIsOrg, roleMap)) {
        userPermissions.push(perm);
      }
    }

    return userPermissions;
  }

  // Check the users roles to see if they have at least one permissions in the permission list.
  // returns an array of the permissions they have that are in the list.
  public static hasCurrentUserPermissionsOriginal({
    permissions,
    currentUser,
    organizationId, // PLease use this.
  }: {
    permissions: PermissionEnum[];
    currentUser: CurrentUser;
    organizationId: string;
  }): PermissionEnum[] {
    if (!currentUser || !currentUser.organizations) {
      throw Auth.Deny({ message: 'Authorization not granted ', authcode: Code.FATAL });
    }

    const thisOrg = getOrgFromCurrentUser({ currentUser, organizationId });
    const rolesIsOrg = (thisOrg && thisOrg.roles) || [];

    const roleMap: RoleMapType = (thisOrg && thisOrg.roleMap) || ({} as RoleMapType);

    if (!roleMap || Object.keys(roleMap).length === 0) {
      throw Auth.Deny({
        message: 'Authorization not granted (no roleMap)',
        authcode: Code.FATAL,
      });
    }

    const userPermissions = [];

    for (const perm of permissions as PermissionEnum[]) {
      if (hasPermission(perm, rolesIsOrg, roleMap)) {
        userPermissions.push(perm);
      }
    }

    if (userPermissions.length > 0) {
      return userPermissions;
    }

    throw Auth.Deny({
      message: ' Authorization not granted (no permissions)',
      authcode: Code.NO_PERMISSION,
      canRetry: true,
    });
  }

  static userHasOrganizationAuthForPermissions({
    // WTF does this name mean ?  Come on PJL you can do better than this.
    permissions, // Any of the permissions will do.
    currentUser,
    policy,
    preemptiveReport,
  }: {
    permissions: PermissionEnum[];
    currentUser: CurrentUser;
    policy: Auth.Policy; // Optional policy to test against instead of the one in organization
    preemptiveReport: PreemptiveSecurityReport;
  }): Auth.RuleResult {
    if (!currentUser || !currentUser.organizations) {
      throw Auth.Deny({ message: ' Authorization not granted', authcode: Code.FATAL });
    }

    try {
      let session = preemptiveReport?.session || currentUser.session;

      session = JSON.parse(JSON.stringify(session));

      const rulesNeeded: { [level in SecurityGroupName]: any } = {} as any;

      for (const perm of permissions) {
        const rule = policy.permissionSecurityGroupMap[perm];

        if (!rule) {
          throw new OrgbrainFatal(
            'Policy rule not found for permission',
            BackendErrorCodesEnum.SystemError
          );
        }
        rulesNeeded[rule] = true;
      }

      const results = [];
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      for (const [key, rule] of Object.entries(policy.securityGroupRuleMap)) {
        if (rulesNeeded[rule.securityGroupName]) {
          const atomicRule = Policy.atomicRuleMap[rule.ruleName];
          const result: Auth.RuleResult = atomicRule.evaluate({
            session,
            params: rule.params,
            preemptiveReport,
          });
          if (result.pass) {
            return result;
          }
          results.push(result);
        }
      }

      throw Auth.Deny({
        message: 'Credentials required',
        authcode: Code.NEED_CREDENTIALS,
        result: results[0],
        canRetry: true,
      });
    } catch (err: unknown) {
      const error = err as AuthError;

      if (!error.authcode) {
        error.authcode = Code.FATAL;
      }
      throw error;
    }
  }
  // Checks if user is allowed to update security settings.
  // If no MFA is set user is allowed
  // Otherwise we check if the user has set the mfaMethod within the last 5 minutes.
  // If fail then throw error.
  static userIsAllowedToUpdateSecuritySettings({
    user,
    preemptiveReport,
  }: {
    user: CurrentUser;
    preemptiveReport?: PreemptiveSecurityReport;
  }): void {
    if (!user) {
      throw Auth.Deny({
        message: 'Authorization not granted because user is null',
        authcode: Code.FATAL,
      });
    }

    preemptiveReport = preemptiveReport || ({} as any);
    let session: Session = preemptiveReport?.session || user?.session;

    if (!session) {
      throw new OrgbrainFatal('No session in userIsAllowedToUpdateSecuritySettings');
    }

    // If user has cognito then we should use that to check if they have it in the session.
    //  DEPRECATED  USeR IS NIW ALLOWED TI TWEAK SETTINGS WITH ANY LOGIN METHOD
    // if (user.identities.find((x) => x.identityType === IdentityType.cognito)) {
    //   if (!session.credentials?.COGNITO) {
    //     const result: Auth.RuleResult = {
    //       pass: false,
    //       mfaCredentialsRequired: [],
    //       idMethodsRequired: [],
    //       reason: 'Cognito required',
    //       expires: -1,
    //       assertLoginCredentials: [], // [CredentialEnum.COGNITO],
    //     };

    //     throw Auth.Deny({
    //       message: 'Credentials required',
    //       authcode: Code.NEED_CREDENTIALS,
    //       result,
    //       canRetry: true,
    //     });
    //   }
    // }

    const mfaMethods: CredentialEnum[] = PermissionService.getAvailable2FAMethods(user);

    // If no mfa methods then we are allowed update security setings.
    if (mfaMethods.length === 0) {
      if (preemptiveReport) {
        preemptiveReport.result = { pass: true, expires: null };
        preemptiveReport.reason = 'User has no MFA methods';
      }
      return;
    }

    const now = preemptiveReport?.nowOverride || Date.now();

    session = JSON.parse(JSON.stringify(session));

    const inactivityTime = now - new Date(session?.lastActivity).getTime() || 0;

    let inactivityPeriod;
    // -- see if we have a mfaMethod that has not expired due to inactivity.
    for (const mfaMethod of mfaMethods) {
      const credentials = user?.session?.credentials;

      // const id = mfa2identity(mfaMethod);

      if (!credentials || !credentials[mfaMethod]) {
        continue;
      }

      if (!credentials[mfaMethod]) {
        continue;
      }

      inactivityPeriod = session.inactivityPeriod?.[mfaMethod];

      if (inactivityPeriod < sharedConfig.securityTiming.userSettingsSecurityTimeOut) {
        preemptiveReport.result = {
          pass: true,
          expires: sharedConfig.securityTiming.userSettingsSecurityTimeOut - inactivityTime,
        };
        preemptiveReport.reason = 'Settings requires recent credentials';
        return;
      }
    }

    // const results: Auth.RuleResult[] = [];

    const result: Auth.RuleResult = {
      pass: false,
      mfaCredentialsRequired: mfaMethods,
      reason: 'Settings requires recent credentials',
      expires: sharedConfig.securityTiming.userSettingsSecurityTimeOut - inactivityPeriod,
    };

    // results.push(result);
    throw Auth.Deny({
      message: 'Credentials required',
      authcode: Code.NEED_CREDENTIALS,
      result,
      canRetry: true,
    });
  }

  static userHasRequiredLoginMFA(user: CurrentUser): Auth.RuleResult {
    const mfaMethods: CredentialEnum[] = PermissionService.getAvailable2FAMethods(user);

    // If no mfa methods then we are allowed update security setings.
    if (!user.mfaMethods?.length) {
      return {
        pass: true,
        mfaCredentialsRequired: [],
        reason: 'User has no login mfa requirements',
        expires: null,
      };
    }

    const session: Session = user?.session;
    const credentials = session?.credentials || [];
    for (const mfaMethod of mfaMethods) {
      if (credentials[mfaMethod]) {
        return {
          pass: true,
          mfaCredentialsRequired: mfaMethods,
          reason: 'Settings requires recent credentials',
          expires: null,
        };
      }
    }

    const result: Auth.RuleResult = {
      pass: false,
      mfaCredentialsRequired: mfaMethods,
      reason: 'MFA for login niot in session',
      expires: 0,
    };

    throw Auth.Deny({
      message: 'Credentials required',
      authcode: Code.NEED_CREDENTIALS,
      result,
      canRetry: true,
    });

    // return result;
  }

  static userHasRequiredId({
    user,
    preemptiveReport,
    requiredId,
  }: {
    user: CurrentUser;
    preemptiveReport?: PreemptiveSecurityReport;
    requiredId: IdentityType[];
  }) {
    if (!user) {
      throw Auth.Deny({
        message: 'Authorization not granted because user is null',
        authcode: Code.FATAL,
      });
    }

    const userIds = (user?.identities || []).map((id) => id.identityType);

    const userIdCred = makeIntoCredentialArray(userIds);
    const requiredSet = new Set(makeIntoCredentialArray(requiredId));

    const intersect = userIdCred.filter((x) => requiredSet.has(x));

    if (intersect.length > 0) {
      return true;
    }

    preemptiveReport = preemptiveReport || ({} as any);

    const mfaMethods: CredentialEnum[] = PermissionService.getAvailable2FAMethods(user);

    let neededMfa = mfaMethods;
    // If no mfa methods then we are allowed update security setings.

    // const now = preemptiveReport?.nowOverride || Date.now();

    let session: Session = preemptiveReport?.session || user?.session;

    session = JSON.parse(JSON.stringify(session));

    // const inactivityTime = now - new Date(session?.lastActivity).getTime() || 0;

    let inactivityPeriod;
    let inactivityPeriodMax = 0;
    // -- see if we have a mfaMethod that has not expired due to inactivity.
    for (const mfaMethod of mfaMethods) {
      const credentials = user?.session?.credentials;

      // const id = mfa2identity(mfaMethod);

      if (!credentials || !credentials[mfaMethod]) {
        continue;
      }

      if (!credentials[mfaMethod]) {
        continue;
      }

      inactivityPeriod = session.inactivityPeriod?.[mfaMethod];

      if (inactivityPeriod > inactivityPeriodMax) {
        inactivityPeriodMax = inactivityPeriod;
      }

      if (inactivityPeriod < sharedConfig.securityTiming.userSettingsSecurityTimeOut) {
        neededMfa = []; // No mfa needed
      }
    }

    const result: Auth.RuleResult = {
      pass: false,
      mfaCredentialsRequired: neededMfa,
      idMethodsRequired: makeIntoCredentialArray(requiredId),
      reason: 'Settings id requires recent credentials',
      expires: sharedConfig.securityTiming.userSettingsSecurityTimeOut - inactivityPeriodMax,
    };

    // results.push(result);
    throw Auth.Deny({
      message: 'Credentials required',
      authcode: Code.NEED_CREDENTIALS,
      result,
      canRetry: true,
    });
  }

  // returns what 2FA methods are available to the user.
  // Note this is different from the mfaMethods which are the ones the user has set for logon
  static getAvailable2FAMethods(user: BaseUser): CredentialEnum[] {
    const idEnums: IdentityType[] = user.identities.map((id) => {
      return id.identityType;
    });

    const credEnums = makeIntoCredentialArray(idEnums);
    return credEnums.filter((cred) => default2FAArrayMap[cred]);
  }

  // This is the basic CLIENT test.  It is used to check if a user has permission to access a client.
  static hasClientPermissionFor({ clientId, user }: { clientId: string; user: CurrentUser }) {
    if (user.superuser || user.superclient) {
      return true;
    }
    if (user.clientRoles[clientId] && Object.keys(user.clientRoles[clientId]).length > 0) {
      return true;
    }

    throw new OrgbrainFatal('No client permission for ' + clientId);
  }

  static getCurrentUserPermissionsTrpc<const P extends readonly PermissionEnum[]>({
    permissions,
    currentUser,
    organizationId,
  }: CurrentUserPermissionPropsTrpc<P>): GenericReturnType<P> {
    if (!currentUser) throw Error('Authorization not granted (no user)');
    if (!currentUser.organizations) throw Error('User has no organizations (no orgs)');

    const result = Object.fromEntries([
      ['any', false],
      ...permissions.map((p) => [p, false]),
    ]) as GenericReturnType<P>;

    const thisOrg = getOrgFromCurrentUser({ currentUser, organizationId });
    const rolesInOrg = (thisOrg && thisOrg.roles) || [];
    const roleMap: RoleMapType = (thisOrg && thisOrg.roleMap) || ({} as RoleMapType);

    let anyPermissionGranted = false;
    for (const perm of permissions) {
      const hasPerm = hasPermission(perm, rolesInOrg, roleMap);
      result[perm] = hasPerm;
      if (hasPerm) anyPermissionGranted = true;
    }
    result.any = anyPermissionGranted;
    return result;
  }
}

type CurrentUserPermissionPropsTrpc<P extends readonly PermissionEnum[]> = {
  permissions: P;
  currentUser: CurrentUser;
  organizationId: string;
};

type GenericReturnType<P extends readonly PermissionEnum[]> = { any: boolean } & {
  [K in P[number]]: boolean;
};
